import * as React from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import ChargebackDisputeController from '../../core/controllers/ChargebackDisputeController'
import { useToast } from '../../core/context/ToastContext'
import {
  EVIDENCE_MAX_FILES,
  formatMegabytes,
  isFinalDisputeError,
  mergeEvidenceFiles,
  validateEvidenceFiles,
} from '../../core/functions/chargebackDispute'

interface DisputeEvidenceDialogProps {
  open: boolean
  disputeId?: string
  onClose: () => void
  onSent: () => void
}

export default function DisputeEvidenceDialog({
  open,
  disputeId,
  onClose,
  onSent,
}: DisputeEvidenceDialogProps) {
  const { showToast } = useToast()
  const [files, setFiles] = React.useState<File[]>([])
  const [sending, setSending] = React.useState(false)
  const [error, setError] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const validation = validateEvidenceFiles(files)

  const reset = () => {
    setFiles([])
    setError('')
  }

  const handleClose = () => {
    if (sending) return
    reset()
    onClose()
  }

  const handleAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files ?? [])
    setFiles((current) => mergeEvidenceFiles(current, added))
    // permite selecionar o mesmo arquivo de novo depois de removê-lo
    e.target.value = ''
  }

  const handleRemove = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index))
  }

  const handleSend = async () => {
    if (!disputeId || !validation.valid || sending) return
    setSending(true)
    setError('')
    try {
      const result = await ChargebackDisputeController.sendEvidences({
        id: disputeId,
        files,
      })
      if (result) {
        setError(result.error)
        if (isFinalDisputeError(result.error)) {
          onSent()
        }
        return
      }
      showToast('Provas enviadas com sucesso', 'success')
      reset()
      onSent()
      onClose()
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={sending}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Contestar chargeback</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="info">
            <strong>Envio único:</strong> todos os arquivos vão juntos e não é
            possível enviar de novo depois. Só PDF, até 2 MB cada, até 10
            arquivos, 10 MB no total e no máximo 10 páginas somando todos.
          </Alert>

          {error !== '' && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {error}
            </Alert>
          )}

          {files.length > 0 &&
            validation.globalErrors.map((message) => (
              <Alert key={message} severity="warning">
                {message}
              </Alert>
            ))}

          {files.length > 0 && (
            <List dense disablePadding>
              {files.map((file, index) => (
                <ListItem
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  disableGutters
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={`Remover ${file.name}`}
                      disabled={sending}
                      onClick={() => handleRemove(index)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <PictureAsPdfIcon
                      color={validation.fileErrors[index] ? 'error' : 'action'}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      <>
                        {formatMegabytes(file.size)} MB
                        {validation.fileErrors[index] && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="error"
                            display="block"
                          >
                            {validation.fileErrors[index]}
                          </Typography>
                        )}
                        {validation.fileWarnings[index] && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="warning.main"
                            display="block"
                          >
                            {validation.fileWarnings[index]}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={sending}
              onClick={() => inputRef.current?.click()}
            >
              Adicionar PDFs
            </Button>
            <Typography variant="caption" color="text.secondary">
              {files.length}/{EVIDENCE_MAX_FILES} arquivos ·{' '}
              {formatMegabytes(validation.totalBytes)}/10 MB
            </Typography>
          </Stack>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            hidden
            onChange={handleAdd}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={sending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!validation.valid || sending}
          startIcon={sending ? <CircularProgress size={16} /> : undefined}
        >
          Enviar provas
        </Button>
      </DialogActions>
    </Dialog>
  )
}
